/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { findIndex } from 'lodash';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { SearchFacet } from '@toucan/common/services/search/constants';

import { Link, LinkSize } from '@toucan/client/shared/components/link';

import { CriteriaList } from '@toucan/client/features/search/components/filter-results-summary/components/criteria-list';
import {
    Filter,
    FilterOption,
} from '@toucan/client/features/search/config/types';

import { CriteriaBoxProps } from './components/criteria-list/components/criteria-box';
import { DEFAULT_TEST_ID } from './constants';
import { styles } from './filter-results-summary.styles';

export type FilterResultsSummaryProps = {
    /**
     * The selected filters
     */
    filters: Filter[];

    /**
     * Handler for when the selected options are updated
     *
     * @param {Filter[]} filters - the updated filters
     */
    onUpdate?: (filters: Filter[]) => void;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
};

type FilterOptionWithIndex = FilterOption & {
    index?: number;
    criteriaLabel?: string;
};

type StyledFilterResultsSummaryProps = Omit<
    FilterResultsSummaryProps,
    'onChange'
>;

const StyledFilterResultsSummary = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<StyledFilterResultsSummaryProps>`
    ${styles}
`;

export const FilterResultsSummary: FunctionComponent<
    FilterResultsSummaryProps
> = ({
    onUpdate,
    filters,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}: FilterResultsSummaryProps) => {
    const { t } = useTranslation();

    const criteriaLabels: Partial<Record<SearchFacet, string>> = {
        [SearchFacet.productPriceModifier]: t('Accessory'),
    };

    // Reduce the selected filter options into a single list and add the
    // filter index to the option so we can later relate it back to the
    // filter in the filters array.
    const activeFilters: FilterOptionWithIndex[] = filters.reduce(
        (acc, filter, index) => {
            return acc.concat(
                filter.selectedOptions.map(
                    (option: FilterOption): FilterOptionWithIndex => {
                        return {
                            ...option,
                            criteriaLabel:
                                criteriaLabels[filter.id] ?? filter.label,
                            index,
                        };
                    }
                )
            );
        },
        [] as FilterOptionWithIndex[]
    );

    /**
     * Remove a filter option from the array of filters and pass the updated
     * filter array to the update callback.
     *
     * @param removedOption the removed option
     */
    function handleRemove(removedOption: FilterOptionWithIndex) {
        // leave early if we have no callback
        if (!onUpdate || removedOption.index === undefined) {
            return;
        }

        // get and remove the filter index from the option
        const filterIndex = removedOption.index;
        delete removedOption.index;

        // find the option index in the filter so we can later remove it
        const removedOptionIndex = findIndex(
            filters[filterIndex].selectedOptions,
            (option: FilterOption) => option.value === removedOption.value
        );

        if (removedOptionIndex === -1) {
            return;
        }

        // get a copy of our filter array
        const updatedFilters = [...filters];

        // remove the option from the filter
        updatedFilters[filterIndex].selectedOptions.splice(
            removedOptionIndex,
            1
        );

        // pass updated filters back out
        onUpdate(updatedFilters);
    }

    /**
     * Removes all the selected filters
     */
    const handleClear = () => {
        // leave early if we have no callback
        if (!onUpdate) {
            return;
        }

        const updatedFilters = [...filters];
        updatedFilters.forEach((filter) => {
            filter.selectedOptions = [];
        });
        onUpdate(updatedFilters);
    };

    return activeFilters.length !== 0 ? (
        <StyledFilterResultsSummary
            filters={filters}
            data-testid={dataTestId}
            {...otherProps}
        >
            <CriteriaList
                data-testid={`${dataTestId}__filters`}
                className="filter-results-list"
                title={t('Filters')}
                criteriaBoxes={activeFilters}
                onRemove={(itemBeingRemoved: CriteriaBoxProps) =>
                    handleRemove(itemBeingRemoved as FilterOptionWithIndex)
                }
            />
            <Link
                size={LinkSize.SMALL}
                className="filter-results-link"
                onClick={handleClear}
                data-testid="filter-results-summary__clear"
            >
                {t('Clear all filters')}
            </Link>
        </StyledFilterResultsSummary>
    ) : null;
};
